import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDownload } from '../api/client';
import { VaultRole, AccountTier, VaultStatus } from '../types/api';
import type { VaultResponse, EstateReadinessResponse, CustomerPortalResponse, VaultSummaryResponse } from '../types/api';
import { parseServerDate } from '../utils/dateUtils';
import { fireConfetti } from '../utils/confetti';
import './VaultHeader.css';

interface Props {
  vault: VaultResponse;
  userRole: VaultRole;
  allVaults?: VaultSummaryResponse[];
  onSwitchVault?: (vaultId: string) => void;
  onTabSelect: (tab: string) => void;
  onNameChange?: (newName: string) => void;
  refreshKey?: number;
  isPreviewMode?: boolean;
  onTogglePreview?: () => void;
}

const roleLabels: Record<number, string> = {
  [VaultRole.Owner]:   'Owner',
  [VaultRole.Spouse]:  'Spouse',
  [VaultRole.Witness]: 'Witness',
  [VaultRole.Advisor]: 'Advisor',
};

const switcherRoleDesc: Record<number, (ownerFirst: string) => string> = {
  [VaultRole.Owner]:   () => 'Your vault',
  [VaultRole.Spouse]:  (n) => `Shared with ${n}`,
  [VaultRole.Witness]: (n) => `Viewing ${n}'s vault`,
  [VaultRole.Advisor]: (n) => `Viewing ${n}'s vault`,
};

function scoreColorClass(score: number): string {
  if (score >= 71) return 'vh-score--green';
  if (score >= 41) return 'vh-score--amber';
  return 'vh-score--red';
}


export default function VaultHeader({
  vault, userRole, allVaults, onSwitchVault, onTabSelect, onNameChange, refreshKey,
  isPreviewMode = false, onTogglePreview,
}: Props) {
  const [readiness, setReadiness] = useState<EstateReadinessResponse | null>(null);
  const [expanded, setExpanded] = useState(false);
  const prevReadinessScore = useRef<number | null>(null);
  const prevVaultId = useRef<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);

  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(vault.name);
  const [nameSaving, setNameSaving] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const isOwner = userRole === VaultRole.Owner;
  const isPaidActive = vault.accountTier === AccountTier.Paid && vault.status === VaultStatus.Active;
  const isForeverFree = vault.accountTier === AccountTier.ForeverFree;

  useEffect(() => {
    const isVaultSwitch = prevVaultId.current !== null && prevVaultId.current !== vault.id;
    if (isVaultSwitch) {
      prevReadinessScore.current = null; // reset so score-100 detection starts fresh for new vault
      fireConfetti('light');
    }
    prevVaultId.current = vault.id;

    apiGet<EstateReadinessResponse>(`/api/vault/${vault.id}/readiness`)
      .then(data => {
        if (data.score === 100 && prevReadinessScore.current !== null && prevReadinessScore.current < 100) {
          fireConfetti('big');
        }
        prevReadinessScore.current = data.score;
        setReadiness(data);
      })
      .catch(() => {});
  }, [vault.id, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDownload() {
    setExportLoading(true);
    try {
      const { blob, fileName } = await apiDownload(`/api/vault/${vault.id}/export`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // silently fail — user will notice no download
    } finally {
      setExportLoading(false);
    }
  }

  async function handleOpenPortal() {
    setPortalLoading(true);
    try {
      const data = await apiPost<CustomerPortalResponse>(`/api/vault/${vault.id}/billing/portal`, {});
      window.location.href = data.portalUrl;
    } catch {
      setPortalLoading(false);
    }
  }

  function startEditing() {
    setNameValue(vault.name);
    setEditingName(true);
    setTimeout(() => nameInputRef.current?.select(), 0);
  }

  function cancelEditing() {
    setEditingName(false);
    setNameValue(vault.name);
  }

  async function saveName() {
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === vault.name) { cancelEditing(); return; }
    setNameSaving(true);
    try {
      await apiPut(`/api/vault/${vault.id}`, { name: trimmed });
      onNameChange?.(trimmed);
      setEditingName(false);
    } catch {
      // leave editor open so user can retry
    } finally {
      setNameSaving(false);
    }
  }

  const createdDate = parseServerDate(vault.createdAt)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) ?? '';

  return (
    <div className="vault-header">
      {/* Preview banner */}
      {isPreviewMode && (
        <div className="vh-preview-banner">
          <span>You're previewing as a family member — read only. This is what your family sees.</span>
          <button className="vh-preview-exit" onClick={onTogglePreview}>Exit Preview</button>
        </div>
      )}

      {/* Top row: vault name + score */}
      <div className="vh-top">
        <div className="vh-name-row">
          {editingName ? (
            <div className="vh-name-editor">
              <input
                ref={nameInputRef}
                className="vh-name-input"
                value={nameValue}
                onChange={e => setNameValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') cancelEditing(); }}
                maxLength={200}
                disabled={nameSaving}
              />
              <button className="vh-name-save" onClick={saveName} disabled={nameSaving}>
                {nameSaving ? '…' : 'Save'}
              </button>
              <button className="vh-name-cancel" onClick={cancelEditing} disabled={nameSaving}>
                Cancel
              </button>
            </div>
          ) : (
            <>
              <div className="vh-name-group">
                <div className="vh-name-line">
                  <h2 className="vh-name">{vault.name}</h2>
                  {isOwner && !isPreviewMode && (
                    <button className="vh-name-edit-btn" onClick={startEditing} title="Rename vault" aria-label="Rename vault">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                  )}
                  {isForeverFree && <span className="vh-ff-badge">Forever Free</span>}
                  {isPreviewMode
                    ? <span className="vh-role-badge vh-role-badge--preview">Previewing</span>
                    : roleLabels[userRole] && (
                      <span className={`vh-role-badge${userRole === VaultRole.Owner ? ' vh-role-badge--owner' : ''}`}>
                        {roleLabels[userRole]}
                      </span>
                    )
                  }
                  {allVaults && allVaults.length > 1 && onSwitchVault && (
                    <button
                      className="vh-switch-btn"
                      onClick={() => setSwitcherOpen(true)}
                      title="Switch vault"
                      aria-label="Switch vault"
                    >
                      Switch Vaults ▾
                    </button>
                  )}
                </div>
                {isPreviewMode && (
                  <p className="vh-witness-label">Previewing as a family member — read only</p>
                )}
                {!isPreviewMode && (userRole === VaultRole.Witness || userRole === VaultRole.Advisor) && (
                  <p className="vh-witness-label">Viewing as {roleLabels[userRole]} — read only</p>
                )}
              </div>
            </>
          )}
        </div>
        {readiness && !isPreviewMode && (
          <button
            className={`vh-score-btn ${scoreColorClass(readiness.score)}`}
            onClick={() => setExpanded(prev => !prev)}
            aria-expanded={expanded}
          >
            <div className="vh-score-inner">
              <span className="vh-score-title">Estate Readiness</span>
              <div className="vh-score-row">
                <span className="vh-score-number">{readiness.score}</span>
                <span className="vh-score-chevron" aria-hidden="true">{expanded ? '▲' : '▼'}</span>
              </div>
            </div>
          </button>
        )}
      </div>

      {/* Expanded checklist */}
      {expanded && readiness && !isPreviewMode && (
        <ul className="vh-checklist">
          {readiness.items.map(item => (
            <li key={item.label} className={`vh-check-item ${item.met ? 'vh-check-item--met' : ''}`}>
              <span className="vh-check-icon" aria-hidden="true">{item.met ? '✓' : '○'}</span>
              <div className="vh-check-text">
                <span className="vh-check-label">{item.label}</span>
                <span className="vh-check-desc">{item.description}</span>
              </div>
              {!item.met && item.tab && (
                <button
                  className="vh-check-action"
                  onClick={() => { onTabSelect(item.tab!); setExpanded(false); }}
                >
                  Add →
                </button>
              )}
              <span className="vh-check-pts">{item.points}pts</span>
            </li>
          ))}
        </ul>
      )}

      {/* Meta row: created date + owner actions */}
      <div className="vh-actions">
        <span className="vh-created">Created {createdDate}</span>
        <div className="vh-actions-right">
          {isOwner && !isPreviewMode && (
            <button className="vh-action-link" onClick={onTogglePreview}>
              Preview as Family
            </button>
          )}
          {isOwner && !isPreviewMode && (
            <button className="vh-action-link" onClick={handleDownload} disabled={exportLoading}>
              {exportLoading ? 'Preparing…' : 'Export Vault (ZIP)'}
            </button>
          )}
          {isOwner && !isPreviewMode && (
            <Link to={`/vault/${vault.id}/print`} className="vh-action-link">Print Summary</Link>
          )}
          {isOwner && isPaidActive && !isPreviewMode && (
            <button className="vh-action-link" onClick={handleOpenPortal} disabled={portalLoading}>
              {portalLoading ? 'Opening…' : 'Manage Billing'}
            </button>
          )}
        </div>
      </div>

      {/* Vault switcher bottom sheet */}
      {switcherOpen && allVaults && onSwitchVault && (
        <>
          <div className="vh-sheet-overlay" onClick={() => setSwitcherOpen(false)} />
          <div className="vh-sheet" role="dialog" aria-label="Switch vault">
            <div className="vh-sheet-header">
              <span className="vh-sheet-title">Your Vaults</span>
              <button className="vh-sheet-close" onClick={() => setSwitcherOpen(false)} aria-label="Close">✕</button>
            </div>
            {allVaults.map(v => {
              const ownerFirst = v.ownerName.split(' ')[0];
              const desc = (switcherRoleDesc[v.userRole] ?? (() => 'Member'))(ownerFirst);
              const isCurrent = v.id === vault.id;
              return (
                <button
                  key={v.id}
                  className={`vh-sheet-item${isCurrent ? ' vh-sheet-item--active' : ''}`}
                  onClick={() => { setSwitcherOpen(false); if (!isCurrent) onSwitchVault(v.id); }}
                >
                  <div className="vh-sheet-item-name">{v.name}</div>
                  <div className="vh-sheet-item-desc">{desc}</div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
